<script lang="ts">
import {
  UnwrapRef,
  computed,
  getCurrentInstance,
  onBeforeMount,
  ref,
  shallowRef,
  watch,
  watchEffect,
} from 'vue';
import { IFloatingFilterParams } from 'ag-grid-enterprise';
import { SelectValues } from '@/types/select';
import Wrapper from './FilterWrapper.vue';
import { isEqual } from 'lodash-es';

interface FilterModel<T> {
  type: 'equals';
  values: T[];
}

export interface LocalFilterModel<T> {
  values: T[];
}

let lastAnchorId = 0;
</script>

<script setup lang="ts" generic="T">
const elCheckboxSelectAll = shallowRef<HTMLInputElement | null>(null);
const elDialog = shallowRef<HTMLDialogElement | null>(null);
const anchorName = ('--_' + (++lastAnchorId).toString(36)) as `--${string}`;

const rlocalFilterModel = ref<LocalFilterModel<T>>({ values: [] });
const localFilterModel = computed({
  get() {
    return rlocalFilterModel.value as LocalFilterModel<T>;
  },
  set(value) {
    (rlocalFilterModel.value as LocalFilterModel<T>) = value;
  },
});

const params = shallowRef<
  IFloatingFilterParams & {
    values: SelectValues<T>;
    noSelectAll?: boolean;
    noSearchBox?: boolean;
  }
>();

const txtFilter = shallowRef('');
const filteredItems = computed(() => {
  if (txtFilter.value === '') return params.value?.values ?? [];
  return (
    params.value?.values.filter(({ text }) =>
      text.toLowerCase().includes(txtFilter.value.toLowerCase())
    ) ?? []
  );
});

const selectedItems = computed(() =>
  localFilterModel.value.values
    .map((value) => params.value?.values.find((v) => v.value === value)?.text)
    .filter(Boolean)
);

defineExpose({
  onParentModelChanged: updateLocalFilter,
});

onBeforeMount(() => {
  params.value = getCurrentInstance()?.proxy.$data.params;
  if (params.value === undefined) return;
  const filterModel = params.value.api.getColumnFilterModel<FilterModel<T>>(params.value.column);
  updateLocalFilter(filterModel);
});

function updateLocalFilter(filterModel: FilterModel<T> | null) {
  filterModel ??= { values: [...knownFilter.values], type: 'equals' };
  localFilterModel.value = { values: [...filterModel.values] };
}

let knownFilter: LocalFilterModel<T> = { values: [] };
watch(rlocalFilterModel, applyFilter, { deep: true, immediate: true });

watchEffect(() => {
  if (elCheckboxSelectAll.value) {
    elCheckboxSelectAll.value.checked =
      filteredItems.value.every((filteredItem) =>
        localFilterModel.value.values.includes(filteredItem.value)
      ) && filteredItems.value.length === localFilterModel.value.values.length;
    elCheckboxSelectAll.value.indeterminate =
      !elCheckboxSelectAll.value.checked && !!localFilterModel.value.values.length;
  }
});

async function applyFilter(newFilter: UnwrapRef<LocalFilterModel<T>>) {
  if (isEqual(newFilter, knownFilter)) {
    // filter not changed, skipping, otherwise we get an endless loop,
    // because of the watch on localFilterModel, being triggered by onParentModelChanged
    return;
  }

  if (params.value === undefined) return;
  knownFilter = structuredClone(localFilterModel.value);
  await params.value.api.setColumnFilterModel<FilterModel<T>>(
    params.value.column,
    localFilterModel.value.values.length === 0
      ? null
      : ({
          ...localFilterModel.value,
          type: 'equals',
        } as FilterModel<T>)
  );
  params.value.api.onFilterChanged();
}

function onSelectAll(e: Event) {
  localFilterModel.value.values = (e.target as HTMLInputElement).checked
    ? filteredItems.value.map(({ value }) => value)
    : [];
}

const keyMap = new WeakMap<WeakKey, string>();
function getKey(value: unknown) {
  switch (typeof value) {
    case 'number':
    case 'string':
    case 'symbol':
    case 'undefined':
      return value;
    case 'object':
    case 'function': {
      if (value === null) return 'null';
      let key = keyMap.get(value);
      if (!key) {
        key = Math.random().toString(36).slice(2);
        keyMap.set(value, key);
      }
      return key;
    }
    case 'boolean':
    case 'bigint':
      return Number(value);
  }
}
</script>

<template>
  <Wrapper :data-test-filter="params?.column.getColId()">
    <div
      class="ag-input-field-input ag-text-field-input"
      style="display: flex; align-items: center; color: white"
      :style="{ 'anchor-name': anchorName }"
      @click="elDialog?.showModal()"
    >
      <div
        v-if="[0, params?.values.length].includes(localFilterModel.values.length)"
        style="margin: 0 auto"
      >
        All
      </div>
      <div v-else :title="selectedItems.join('\n')">
        {{ selectedItems.join(', ') }}
      </div>
    </div>
    <dialog
      ref="elDialog"
      class="ag-multiple-select"
      closedby="any"
      :style="{
        'position-anchor': anchorName,
        padding: '2px',
      }"
    >
      <div v-if="!params?.noSearchBox" style="padding: 4px">
        <input v-model="txtFilter" class="ag-input-field-input ag-text-field-input" />
      </div>
      <label v-if="!params?.noSelectAll">
        <input ref="elCheckboxSelectAll" type="checkbox" @input="onSelectAll" />
        <span>Select all</span>
      </label>
      <hr v-if="!params?.noSelectAll" style="margin: 4px 0; border-color: var(--ag-border-color)" />
      <div v-if="filteredItems.length === 0" style="padding: 4px">No items found</div>
      <div v-else style="max-height: 250px; overflow-y: auto; min-width: fit-content">
        <label v-for="value in filteredItems" :key="getKey(value.value)">
          <input v-model="localFilterModel.values" type="checkbox" :value="value.value" />
          <span>{{ value.text }}</span>
        </label>
      </div>
    </dialog>
  </Wrapper>
</template>

<style lang="scss">
.ag-multiple-select[open] {
  position: fixed;
  left: anchor(left);
  top: anchor(bottom);
  right: anchor(right);
  margin-top: 8px;
  display: grid;
  align-items: center;
  justify-content: center;
  border: var(--ag-borders) var(--ag-border-color);
  label {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px;
    cursor: pointer;
  }
  position-try: --custom-top;
}

@position-try --custom-top {
  top: unset;
  bottom: anchor(top);
  margin-bottom: 10px;
}
</style>
